import { useState, useRef, KeyboardEvent, useMemo } from "react";
import { FolderTreeItem as FolderTreeItemType } from "@zapier/client";
import FolderTreeItem from "../FolderTreeItem/FolderTreeItem";
import styles from "./FolderTreeBranch.module.css";
import { getFoldersIndexUrl, getFolderUrl } from "@zapier/utils";
import classnames from "classnames";

export interface FolderTreeBranchProps {
  folders: FolderTreeItemType[];
  selectedFolderId?: string;
  level?: number;
  isRoot?: boolean;
  expandedFolders?: Set<string | undefined>;
  onToggleExpanded?: (folderId?: string) => void;
  groupId?: string;
  isNavigation?: boolean;
  onFolderClick?: (folder: FolderTreeItemType) => void;
  focusableFolderId?: string;
  handleKeyDown?: (event: KeyboardEvent, folder: FolderTreeItemType) => void;
  getFocusableTabIndex?: (folderId: string) => 0 | -1;
  treeRef?: React.RefObject<HTMLElement>;
  rootAsAccordion?: boolean;
}

const FolderTreeBranch = ({
  folders,
  selectedFolderId,
  onFolderClick,
  level = 0,
  isRoot = false,
  expandedFolders = new Set(),
  onToggleExpanded,
  groupId,
  isNavigation = false,
  rootAsAccordion = false,
  focusableFolderId,
  handleKeyDown: handleKeyDownFromHook,
  getFocusableTabIndex,
  treeRef,
}: FolderTreeBranchProps) => {
  const [internalExpandedFolders, setInternalExpandedFolders] =
    useState<Set<string | undefined>>(expandedFolders);

  const internalTreeReference = useRef<HTMLUListElement>(null);
  // Use provided ref for root, internal ref for nested branches
  const treeReference = (
    isRoot && treeRef ? treeRef : internalTreeReference
  ) as React.RefObject<HTMLUListElement>;
  const currentExpandedFolders = onToggleExpanded
    ? expandedFolders
    : internalExpandedFolders;
  const toggleExpanded =
    onToggleExpanded ||
    ((folderId?: string) => {
      const newExpanded = new Set(internalExpandedFolders);
      if (newExpanded.has(folderId)) {
        newExpanded.delete(folderId);
      } else {
        newExpanded.add(folderId);
      }
      setInternalExpandedFolders(newExpanded);
    });

  // Use keyboard handler from hook if provided, otherwise no keyboard navigation
  const handleKeyDown =
    handleKeyDownFromHook ||
    (() => {
      // No-op fallback for when hook is not used
    });

  interface FolderTreeItemRendererProps {
    folder: FolderTreeItemType;
    index: number;
    selectedFolderId?: string;
    isRoot: boolean;
    currentExpandedFolders: Set<string | undefined>;
    onFolderClick?: (folder: FolderTreeItemType) => void;
    isNavigation: boolean;
    handleKeyDown: (event: KeyboardEvent, folder: FolderTreeItemType) => void;
    toggleExpanded: (folderId?: string) => void;
    level: number;
    getFocusableTabIndex?: (folderId: string) => 0 | -1;
    rootAsAccordion: boolean;
  }

  const FolderTreeItemRenderer = ({
    folder,
    index,
    selectedFolderId,
    isRoot,
    currentExpandedFolders,
    onFolderClick,
    isNavigation,
    handleKeyDown,
    toggleExpanded,
    level,
    getFocusableTabIndex,
    rootAsAccordion,
  }: FolderTreeItemRendererProps) => {
    const folderId = String(folder.id);
    const isExpanded = currentExpandedFolders.has(folderId);
    const isSelected = useMemo(
      () =>
        selectedFolderId === folderId ||
        (selectedFolderId === "root" && isRoot),
      [selectedFolderId, folderId, isRoot],
    );
    const hasChildren = !!folder.children?.length;
    const childGroupId = hasChildren
      ? `folder-${folder.id}-children`
      : undefined;

    // Use getFocusableTabIndex from hook if available, otherwise fallback to old logic
    const shouldBeTabbable = getFocusableTabIndex
      ? getFocusableTabIndex(folderId) === 0
      : isSelected || (!selectedFolderId && isRoot && index === 0);

    // When the root node acts as a collapsible accordion header, clicking
    // the whole row toggles expand — it does not navigate or become "active".
    const isAccordionHeader = isRoot && rootAsAccordion;

    return (
      <li
        role="none"
        className={classnames([
          styles.treeItem,
          !hasChildren && styles.noChildren,
        ])}
        data-folder-id={folderId}
      >
        <FolderTreeItem
          text={folder.name}
          isSelected={isAccordionHeader ? false : isSelected}
          onClick={
            isAccordionHeader
              ? () => toggleExpanded(folderId)
              : () => onFolderClick?.(folder)
          }
          href={
            isAccordionHeader || !isNavigation
              ? undefined
              : isRoot
                ? getFoldersIndexUrl()
                : getFolderUrl(folderId)
          }
          role="treeitem"
          aria-current={
            isAccordionHeader ? undefined : isSelected ? "page" : undefined
          }
          aria-expanded={!!hasChildren && isExpanded}
          tabIndex={shouldBeTabbable ? 0 : -1}
          onKeyDown={(event) =>
            handleKeyDown(event as unknown as KeyboardEvent, folder)
          }
          aria-owns={childGroupId}
          onToggleExpanded={toggleExpanded}
          folderId={folder.id}
          isShared={folder.isShared}
          isAccordionHeader={isAccordionHeader}
        />

        {hasChildren && isExpanded && (
          <FolderTreeBranch
            folders={folder.children as FolderTreeItemType[]}
            selectedFolderId={selectedFolderId}
            onFolderClick={onFolderClick}
            level={level + 1}
            isRoot={false}
            expandedFolders={currentExpandedFolders}
            onToggleExpanded={toggleExpanded}
            groupId={childGroupId}
            isNavigation={isNavigation}
            focusableFolderId={focusableFolderId}
            handleKeyDown={handleKeyDown}
            getFocusableTabIndex={getFocusableTabIndex}
            treeRef={treeRef}
          />
        )}
      </li>
    );
  };

  return (
    <ul
      ref={treeReference}
      role={isRoot ? "tree" : "group"}
      aria-label={isRoot ? "Folders" : undefined}
      id={groupId}
      className={classnames(styles.treeList, isRoot && styles.rootTreeList)}
    >
      {folders.map((folder, index) => (
        <FolderTreeItemRenderer
          key={folder.id}
          folder={folder}
          index={index}
          selectedFolderId={selectedFolderId}
          isRoot={isRoot}
          currentExpandedFolders={currentExpandedFolders}
          onFolderClick={onFolderClick}
          isNavigation={isNavigation}
          handleKeyDown={handleKeyDown}
          toggleExpanded={toggleExpanded}
          level={level}
          getFocusableTabIndex={getFocusableTabIndex}
          rootAsAccordion={rootAsAccordion}
        />
      ))}
    </ul>
  );
};

export default FolderTreeBranch;
